import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { catchError, switchMap, finalize, take, first, timeout, tap } from 'rxjs/operators';
import { Observable, throwError, timer, of, TimeoutError } from 'rxjs';
import { NotificationService } from 'src/app/services/shared/notification/notification.service';
import { NotificationAlertService } from 'src/app/components/shared/notifications-alert/service/notifications-alert.service';
import { SessionService } from 'src/app/services/shared/session/session.service';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/services/shared/local-storage/local-storage.service';
import { GenerateTokenService, Token } from 'integrationpingid-lib';
import { SpinnerService } from 'src/app/services/shared/spinner/spinner.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {

  private corsNotificationTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly CORS_NOTIFICATION_DELAY_MS = 3_000;

  /** Mensajes amigables por código HTTP y patrón de URL. El primer patrón que coincida gana. */
  private static readonly FRIENDLY_MESSAGES: Partial<Record<number, {
    default: string;
    patterns?: Array<{ url: RegExp; message: string }>;
  }>> = {
    400: {
      default: 'Los datos de la solicitud no son válidos.',
      patterns: [
        { url: /kl-service|getEntityById/, message: 'Documento no válido para la búsqueda. Verifique el valor ingresado.' },
      ]
    },
    404: {
      default: 'No se encontraron resultados.',
      patterns: [
        { url: /journaling-service/, message: 'No hay registros disponibles para esta fuente.' },
        { url: /kl-service|getEntityById/, message: 'Entidad no encontrada. Verifique el documento ingresado.' },
        { url: /v4\/sources|gfd\/configs/, message: 'No se encontraron datos para esta fuente.' },
      ]
    },
    500: { default: 'Error en el servidor. Intente nuevamente más tarde.' },
    502: { default: 'El servicio no está disponible en este momento. Intente más tarde.' },
    503: { default: 'El servicio está temporalmente fuera de servicio.' },
    504: { default: 'El tiempo de espera del servidor fue superado. Intente más tarde.' },
  };

  constructor(
    private injector: Injector,
    private localStorageService: LocalStorageService,
    public serviceToken: GenerateTokenService,
    private sessionService: SessionService,
    private spinnerService: SpinnerService
  ) { }

  private scheduleCorsNotification(url: string, notifierAlert: NotificationAlertService): void {
    const key = url.split('?')[0];
    if (this.corsNotificationTimers.has(key)) {
      clearTimeout(this.corsNotificationTimers.get(key)!);
    }
    const timerId = setTimeout(() => {
      notifierAlert.show({
        title: `Error de conexión con el servidor (posible CORS o red). Servicio: ${url}`,
        type: 'error',
        mode: 'component',
        position: 'top-right',
        color: '#fff',
        maxWidth: '960px'
      });
      this.corsNotificationTimers.delete(key);
    }, ErrorInterceptor.CORS_NOTIFICATION_DELAY_MS);
    this.corsNotificationTimers.set(key, timerId);
  }

  private clearCorsNotification(url: string): void {
    const key = url.split('?')[0];
    if (this.corsNotificationTimers.has(key)) {
      clearTimeout(this.corsNotificationTimers.get(key)!);
      this.corsNotificationTimers.delete(key);
    }
  }

  private getErrorDetail(err: HttpErrorResponse): string {
    const body = err.error;
    if (typeof body === 'string') {
      return body;
    }
    const details = body?.additionalErrorDetails;
    const raw = body?.message || body?.error || body?.detail || details?.message || details?.error || body?.description;
    if (typeof raw !== 'string' || !raw.trim()) {
      return err.statusText || 'Error inesperado';
    }
    // El gateway devuelve "400 Bad Request from POST http://host-interno:8080/...": se descarta la URL interna y el codigo repetido.
    const detail = raw.split(' from ')[0].replace(/^\d{3}\s+/, '').trim();
    return detail || err.statusText || 'Error inesperado';
  }

  private waitForOidcToken(): Observable<string | null> {
    return timer(0, 100).pipe( // Emite un valor cada 100 milisegundos.
      switchMap(() => {
        const oidcStorage = localStorage.getItem('oidc-token-storage');
        if (oidcStorage) {
          try {
            const tokenData = JSON.parse(oidcStorage);
            // Formato nuevo (OIDC): accessToken es un string directo
            // Formato antiguo (Oidc SDK): accessToken es un objeto con { accessToken: "jwt..." }
            const rawAccessToken = tokenData?.accessToken;
            const token = typeof rawAccessToken === 'string'
              ? rawAccessToken
              : rawAccessToken?.accessToken || localStorage.getItem('access_token');
            return of(token);
          } catch (error) {
            console.error('Error parsing oidc-token-storage:', error);
            return of(null); // Emite null si hay un error al analizar el JSON.
          }
        }
        return of(undefined); // Emite undefined si 'oidc-token-storage' no existe.
      }),
      take(500), // Se suscribe y emite valores hasta por 50 segundos (500 * 100ms).
      first(token => token !== undefined, null) // Emite el primer token que no sea undefined. Si se completa el observable sin emitir un token no undefined, emite null.
    );
  }

  private isTokenEndpoint(url: string): boolean {
    const normalizedUrl = (url || '').toLowerCase();
    return normalizedUrl.includes('/as/token')
      || normalizedUrl.includes('token.oauth2')
      || normalizedUrl.includes('/v2/oauth/token');
  }

  private getVipCommerceUrl(): string {
    return this.localStorageService.configInit?.['F_OKTA_COMMERCE_URL']
      || environment.OKTA_COMMERCE_URL
      || '';
  }

  private getClientFriendlyMessage(status: number, url: string): string | null {
    const config = ErrorInterceptor.FRIENDLY_MESSAGES[status];
    if (!config) return null;
    return config.patterns?.find(p => p.url.test(url))?.message ?? config.default;
  }

  /**
   * Metodo intercept implementado de la interface HttpInterceptor
   * Captura errores y devuelve mensaje correspondiente al usuario
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.sessionService.tokenPingIDValidationSession();

    const skipSpinner = request.headers.get('x-skip-spinner') === 'true';
    if (skipSpinner) {
      request = request.clone({ headers: request.headers.delete('x-skip-spinner') });
    }

    // console.log("request ------>", request);
    if (!skipSpinner) {
      this.spinnerService.show();
    }
    const url = request.url;
    const isTokenEndpoint = this.isTokenEndpoint(url);
    const validate = url.includes(`supplier-information`)
      || url.includes(`/contrib-gen-service`)
      || url.includes('one_per_one')
      || url.includes('contribution-api');
    // let token: Token = this.serviceToken.getTokenUser();
    // const authorization = this.localStorageService.getIsVip ? localStorage.getItem('oidc-token-storage[idtoken][idtoken]') : `${token.token_type} ${token.access_token}`;
    const notifier = this.injector.get(NotificationService);
    const notifierAlert = this.injector.get(NotificationAlertService);
    const loginStatus = localStorage.getItem('loginStatus');

    if (environment.USE_LOGIN.toString().toLowerCase() === "true" && !this.localStorageService.getIsVip) {
      if (loginStatus === "Ok") {
        const token = localStorage.getItem('access_token');
        const expirationValue = localStorage.getItem('token_expiration');
        const expirationDate = expirationValue ? new Date(expirationValue) : new Date(0);
        const currentDate = new Date();

        if (token && expirationDate > currentDate) {
          // this is intentional
        } else {
          // notifier.muestraMensaje("¡Su sesión ha Expirado!, será redirigo al inicio");
          // setTimeout(() => {
          //   localStorage.clear();
          //   sessionStorage.clear();
          //   this.router.navigateByUrl(this.localStorageService.getIsVip
          // ? environment.URL_RETURN_APP_VIP
          // : this.localStorageService.getIsExternoRDS
          //   ? environment.URL_RETURN_APP_RDS
          //   : environment.URL_RETURN_APP)
          //     .then(() => window.location.reload());
          // }, 3000);
        }
      }
    }

    if (this.localStorageService.getIsVip) { // VIP con token OIDC persistido en localStorage
      // En DM no se usa angular-auth-oidc-client para refresco automatico con silentRenew/useRefreshToken.
      // El ciclo de renovacion VIP se controla desde SessionService (activity-based + redirect a Commerce-CIAM).
      return this.waitForOidcToken().pipe(
        switchMap(oidcAccessToken => {
          // console.log("oidcAccessToken ---->", oidcAccessToken);
          let modifiedRequest = request;
          if (oidcAccessToken && !isTokenEndpoint && !validate) {
            modifiedRequest = request.clone({
              setHeaders: {
                Authorization: 'Bearer ' + oidcAccessToken,
                ...(!request.headers.has('Content-Type') && { 'Content-Type': 'application/json' }),
              },
            });
          } else if ((validate) && !isTokenEndpoint) {
            const token = localStorage.getItem('tokenAPIGEE') || '';
            modifiedRequest = request.clone({
              setHeaders: {
                Authorization: 'Bearer ' + token,
                ...(request.method !== 'GET' && !request.headers.has('Content-Type') && { 'Content-Type': 'application/json' }),
              },
            });
          }
          return next.handle(modifiedRequest).pipe(
            tap(() => this.clearCorsNotification(modifiedRequest.url)),
            timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
            catchError((err: HttpErrorResponse | TimeoutError | any) => {
              if (err instanceof TimeoutError) {
                const segundos = Math.round(Number(environment.HTTP_REQUEST_TIMEOUT) / 1000);
                notifierAlert.show({
                  title: `La solicitud al servidor superó el tiempo máximo de espera (${segundos} seg). La operación fue cancelada.`,
                  type: 'error',
                  mode: 'component',
                  position: 'top-right',
                  color: '#fff',
                  maxWidth: '960px'
                });
                return throwError(() => err);
              }
              // Error CORS o de red: status 0 sin respuesta del servidor
              if (err instanceof HttpErrorResponse && err.status === 0) {
                const serviceUrl = err.url || modifiedRequest.url || 'desconocido';
                this.scheduleCorsNotification(serviceUrl, notifierAlert);
                return throwError(() => err);
              }
              let mensajeError: any = "";
              if (err.error instanceof ErrorEvent) {
                mensajeError = `Mensaje: ${err.error.message}`;
              } else {
                const errorDetail = this.getErrorDetail(err);
                mensajeError = `Código: ${err.status}, Mensaje: ${errorDetail}`;
                this.switchErrors(err, request, notifier, mensajeError, notifierAlert);
              }
              return throwError(() => mensajeError);
            })
          );
        }),
        finalize(() => {
          if (!skipSpinner) {
            this.validateTimeoutConfigs(url);
          }
        })
      );
    } else { // PINGID / RDS externo
      const token: Token = this.serviceToken.getTokenUser();
      const rdsAccessToken = this.localStorageService.getIsExternoRDS
        ? localStorage.getItem('access_token')
        : null;

      // console.log("validate --->", validate)

      if (rdsAccessToken && !isTokenEndpoint && !validate) {
        request = request.clone({
          setHeaders: {
            Authorization: 'Bearer ' + rdsAccessToken,
          },
        });
      } else if (token && !isTokenEndpoint && !validate) {
        request = request.clone({
          setHeaders: {
            Authorization: token.token_type + " " + token.access_token,
          },
        });
      } else if ((validate) && !isTokenEndpoint) {

        const token = localStorage.getItem('tokenAPIGEE') || '';
        // console.log("interceptor tokeb ------>", token);
        request = request.clone({
          setHeaders: {
            Authorization: 'Bearer ' + token,
          },
        });
      }
      return next.handle(request).pipe(
        tap(() => this.clearCorsNotification(request.url)),
        timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
        catchError((err: HttpErrorResponse | TimeoutError | any) => {
          if (err instanceof TimeoutError) {
            const segundos = Math.round(Number(environment.HTTP_REQUEST_TIMEOUT) / 1000);
            notifierAlert.show({
              title: `La solicitud al servidor superó el tiempo máximo de espera (${segundos} seg). La operación fue cancelada.`,
              type: 'error',
              mode: 'component',
              position: 'top-right',
              color: '#fff',
              maxWidth: '960px'
            });
            return throwError(() => err);
          }
          // Error CORS o de red: status 0 sin respuesta del servidor
          if (err instanceof HttpErrorResponse && err.status === 0) {
            const serviceUrl = err.url || request.url || 'desconocido';
            this.scheduleCorsNotification(serviceUrl, notifierAlert);
            return throwError(() => err);
          }
          let mensajeError: any = "";
          if (err.error instanceof ErrorEvent) {
            mensajeError = `Mensaje: ${err.error.message}`;
          } else {
            const errorDetail = this.getErrorDetail(err);
            mensajeError = `Código: ${err.status}, Mensaje: ${errorDetail}`;
            this.switchErrors(err, request, notifier, mensajeError, notifierAlert);
          }
          return throwError(() => mensajeError);
        }),
        finalize(() => {
          if (!skipSpinner) {
            this.validateTimeoutConfigs(url);
          }
        })
      );
    }
  }

  switchErrors(err: HttpErrorResponse, request: HttpRequest<any>, notifier: NotificationService, mensajeError: any, notifierAlert: NotificationAlertService) {
    switch (err.status) {
      case 200:
        break;
      case 400:
        if (request.method == "GET") {
          // this is intentional
        } else {
          if (request.method == "POST" || request.method == "PATCH") {
            notifierAlert.show({
              title: this.getClientFriendlyMessage(400, request.url) ?? mensajeError,
              type: 'error',
              mode: 'component',
              position: 'top-right',
              color: '#fff',
              maxWidth: '960px'
            });
          } else {
            // this is intentional
          }
        }
        break;
      case 401:
        if (this.localStorageService.getIsVip) {
          notifierAlert.show({
            title: 'Sesión expirada, redirigiendo a Portal',
            type: 'error',
            mode: 'component',
            position: 'top-right',
            color: '#fff',
            maxWidth: '960px'
          });
          const commerceUrl = this.getVipCommerceUrl();
          if (commerceUrl) {
            window.location.href = commerceUrl;
          }
        }
        break;
      case 403:
        if (this.localStorageService.getIsVip) {
          notifierAlert.show({
            title: 'Sesión expirada o sin permisos. Redirigiendo al Portal.',
            type: 'error',
            mode: 'component',
            position: 'top-right',
            color: '#fff',
            maxWidth: '960px'
          });
          const commerceUrl = this.getVipCommerceUrl();
          if (commerceUrl) {
            window.location.href = commerceUrl;
          }
        } else {
          notifierAlert.show({
            title: 'Sesión expirada o sin permisos. Debe volver a iniciar sesión.',
            type: 'error',
            mode: 'component',
            position: 'top-right',
            color: '#fff',
            maxWidth: '960px'
          });
          setTimeout(() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = environment.URL_RETURN_APP || '/';
          }, 1500);
        }
        break;
      case 404:
        // Silenciar 404 del endpoint GET profiles por región (se maneja en el servicio)
        if (request.url.includes('/v1/profiles/')) {
          break;
        }
        // No mostrar errores para GET requests de Purposing (purp/list, /purp/teams/ y purp/retrieve)
        if (request.method === "GET" && (request.url.includes('/purp/list/') || request.url.includes('/purp/teams/') || request.url.includes('/purp/retrieve/'))) {
          break;
        }
        if (!this.localStorageService.getIsVip) {
          if (err.error.error == "data not found") {
            // this is intentional
          } else if (err.error.error == "Insufficient buffer size for the query.") {
            notifierAlert.show({
              title: 'Tamaño de Buffer insuficiente, favor acote su búsqueda',
              type: 'error',
              mode: 'component',
              position: 'top-right',
              color: '#fff',
              maxWidth: '960px'
            });
          } else {
            notifierAlert.show({
              title: this.getClientFriendlyMessage(404, request.url) ?? mensajeError,
              type: 'error',
              mode: 'component',
              position: 'top-right',
              color: '#fff',
              maxWidth: '960px'
            });
          }
        }
        break;
      case 500:
        if (this.localStorageService.getIsVip) {
          if (typeof err['additionalErrorDetails'] !== 'undefined') {
            if (typeof err['additionalErrorDetails']['message'] !== 'undefined') {
              if (err['additionalErrorDetails']['message'] === 'The JWT cannot be validated') {
                notifierAlert.show({
                  title: 'Sesión expirada, redirigiendo a Portal.',
                  type: 'error',
                  mode: 'component',
                  position: 'top-right',
                  color: '#fff',
                  maxWidth: '960px'
                });
                const commerceUrl = this.getVipCommerceUrl();
                if (commerceUrl) {
                  window.location.href = commerceUrl;
                }
              } else {
                // this is intentional
                //notifier.showError(mensajeError);
              }
            } else {
              // this is intentional
              //notifier.showError(mensajeError);
            }
          } else {
            // this is intentional
            //notifier.showError(mensajeError);
          }
        } else {
          notifierAlert.show({
            title: this.getClientFriendlyMessage(500, request.url) ?? mensajeError,
            type: 'error',
            mode: 'component',
            position: 'top-right',
            color: '#fff',
            maxWidth: '960px'
          });
        }
        break;
      case 504:
      case 503:
        notifierAlert.show({
          title: this.getClientFriendlyMessage(err.status, request.url) ?? mensajeError,
          type: 'error',
          mode: 'component',
          position: 'top-right',
          color: '#fff',
          maxWidth: '960px'
        });
        break;
      default:
        notifierAlert.show({
          title: this.getClientFriendlyMessage(err.status, request.url) ?? mensajeError,
          type: 'error',
          mode: 'component',
          position: 'top-right',
          color: '#fff',
          maxWidth: '960px'
        });
        break;
    }
  }

  validateTimeoutConfigs(url) {
    if (url.includes('v1/gfd/configs')) {
      setTimeout(() => { this.spinnerService.hide() }, 1000);
    } else {
      this.spinnerService.hide();
    }
  }
}
